import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

/**
 * Utility functions for formatting data consistently across the project.
 */

/**
 * Định dạng tiền tệ chuẩn.
 * Mặc định sử dụng định dạng kiểu Việt Nam (vi-VN) và hậu tố "đ".
 *
 * @example
 * formatCurrency(1250000) // "1.250.000 đ"
 */
export function formatCurrency(value: number | string | null | undefined, suffix: string = 'VNĐ'): string {
  if (value === null || value === undefined || value === '') {
    return `0 ${suffix}`;
  }

  const num = typeof value === 'string' ? Number(value) : value;
  if (isNaN(num)) return `0 ${suffix}`;

  return `${num.toLocaleString('vi-VN')} ${suffix}`;
}

/**
 * Định dạng số tiền lớn thành dạng thu gọn có chữ (tr, tỷ).
 * Phù hợp để hiển thị trên KPI Dashboards.
 *
 * @example
 * formatCompactCurrency(1250000000) // "1,2 tỷ"
 * formatCompactCurrency(5000000) // "5 tr"
 */
export function formatCompactCurrency(value: number | string | null | undefined, suffix: string = ''): string {
  if (value === null || value === undefined || value === '') {
    return `0${suffix ? ' ' + suffix : ''}`;
  }

  const num = typeof value === 'string' ? Number(value) : value;
  if (isNaN(num) || num === 0) {
    return `0${suffix ? ' ' + suffix : ''}`;
  }

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  let result = '';

  if (absNum >= 1_000_000_000) {
    const bill = absNum / 1_000_000_000;
    result = `${sign}${bill.toLocaleString('vi-VN', { maximumFractionDigits: 1 })} tỷ`;
  } else if (absNum >= 1_000_000) {
    const mill = absNum / 1_000_000;
    result = `${sign}${mill.toLocaleString('vi-VN', { maximumFractionDigits: 1 })} tr`;
  } else {
    result = num.toLocaleString('vi-VN');
  }

  return suffix ? `${result} ${suffix}` : result;
}

/**
 * Định dạng tiền theo đơn vị tỷ, dùng cho dashboard dự án.
 *
 * @example
 * formatBillionCurrency(256800000000) // "256,8"
 */
export function formatBillionCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0';
  const num = typeof value === 'string' ? Number(value) : value;
  if (isNaN(num) || num === 0) return '0';
  return (num / 1_000_000_000).toLocaleString('vi-VN', { maximumFractionDigits: 1 });
}

/**
 * Định dạng ngày tháng chuẩn Việt Nam (dd/MM/yyyy).
 */
export function formatDate(date: string | Date | number | null | undefined, formatStr: string = 'dd/MM/yyyy'): string {
  if (!date) return '---';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '---';
    return format(d, formatStr, { locale: vi });
  } catch (e) {
    return '---';
  }
}

/**
 * Định dạng ngày tháng dạng đầy đủ (Ngày dd tháng MM năm yyyy).
 */
export function formatDateLong(date: string | Date | number | null | undefined): string {
  if (!date) return '---';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '---';
    return format(d, "'Ngày' dd 'tháng' MM 'năm' yyyy", { locale: vi });
  } catch (e) {
    return '---';
  }
}

/**
 * Định dạng ngày giờ đầy đủ (Thứ..., ngày dd/MM/yyyy HH:mm).
 */
export function formatFullDate(date: string | Date | number | null | undefined): string {
  if (!date) return '---';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '---';
    return format(d, "eeee, 'ngày' dd/MM/yyyy", { locale: vi });
  } catch (e) {
    return '---';
  }
}

/**
 * Định dạng ngày giờ (dd/MM/yyyy HH:mm).
 */
export function formatDateTime(date: string | Date | number | null | undefined): string {
  if (!date) return '---';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '---';
    return format(d, 'dd/MM/yyyy HH:mm', { locale: vi });
  } catch (e) {
    return '---';
  }
}
/**
 * Chuyển đổi ngày tháng sang định dạng cho input type="date" (yyyy-MM-dd).
 */
export function toInputDate(date: string | Date | number | null | undefined): string {
  if (!date) return '';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return format(d, 'yyyy-MM-dd');
  } catch (e) {
    return '';
  }
}

/**
 * Tính số ngày còn lại so với hiện tại.
 */
export function getDaysRemaining(endDate: string | Date | number): number {
  if (!endDate) return 0;
  const end = new Date(endDate).getTime();
  const now = new Date().getTime();
  const diff = end - now;
  return Math.max(0, Math.ceil(diff / (1000 * 3600 * 24)));
}

/**
 * Viết hoa chữ cái đầu tiên của chuỗi.
 */
export function capitalizeFirstLetter(text: string | null | undefined): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Viết hoa toàn bộ chuỗi ().
 */
export function capitalize(text: string | null | undefined): string {
  if (!text) return '';
  return text.toUpperCase();
}
